// src/api/user.ts
import { apiClient } from './client';
import type { ApiResponse, Address, City, User } from '../types';

export const userAPI = {

  async getProfile(): Promise<ApiResponse<User>> {
    try {
      const response = await apiClient.get<User>('/users/profile/');
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.error || 'Failed to fetch profile',
        status: error.response?.status || 500,
      };
    }
  },

  // Update user profile
  async updateProfile(profileData: {
    first_name?: string;
    last_name?: string;
    middle_name?: string;
    phone?: string;
    date_of_birth?: string;
  }): Promise<ApiResponse<User>> {
    try {
      const response = await apiClient.patch<User>('/users/update_profile/', profileData);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.error || error.response?.data?.detail || 'Failed to update profile',
        status: error.response?.status || 500,
      };
    }
  },

  async uploadIdDocument(file: File): Promise<ApiResponse<{
    uploaded_at: string;
    verification_status: string;
    message: string;
  }>> {
    try {
      const formData = new FormData();
      formData.append('id_document', file);

      const response = await apiClient.post<{
        uploaded_at: string;
        verification_status: string;
        message: string;
      }>(
        '/users/upload_id/',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.error || error.response?.data?.detail || 'Failed to upload ID',
        status: error.response?.status || 500,
      };
    }
  },

  async viewIdDocument(userId?: number): Promise<Blob> {
    const url = userId ? `/users/${userId}/view_id/` : '/users/view_my_id/';
    const response = await apiClient.get(url, { responseType: 'blob' });
    return response.data;
  },

  // Change password
  async changePassword(passwordData: {
    current_password: string;
    new_password: string;
  }): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.post('/users/change-password/', passwordData);
      return { data: undefined, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to change password',
        status: error.response?.status || 500,
      };
    }
  },

  // Upload profile picture
  async uploadProfilePicture(file: File): Promise<ApiResponse<{ profile_picture: string }>> {
    try {
      const formData = new FormData();
      formData.append('profile_picture', file);

      const response = await apiClient.post('/users/profile-picture/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to upload profile picture',
        status: error.response?.status || 500,
      };
    }
  },

  async updateProfileName(fullName: string): Promise<ApiResponse<User>> {
    try {
      const response = await apiClient.patch<User>('/users/profile/', { full_name: fullName });
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to update name',
        status: error.response?.status || 500,
      };
    }
  },
  async replaceIdDocument(file: File): Promise<ApiResponse<{
    uploaded_at: string;
    verification_status: string;
    message: string;
  }>> {
    try {
      const formData = new FormData();
      formData.append('id_document', file);

      const response = await apiClient.post(
        '/users/replace_id/',  // Different endpoint
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.error || 'Failed to replace ID',
        status: error.response?.status || 500,
      };
    }
  },

 // Get all users (admin only)
  async getUsers(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    role?: string;
    is_active?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.get('/auth/users/', { params });
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch users',
        status: error.response?.status || 500
      };
    }
  },

  // Get available drivers (staff only)
  async getAvailableDrivers(): Promise<ApiResponse<any[]>> {
    try {
      const response = await apiClient.get('/auth/users/drivers/available/');
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch available drivers',
        status: error.response?.status || 500
      };
    }
  },

  // Get driver by ID
  async getDriver(driverId: number): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.get(`/auth/users/${driverId}/`);
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to fetch driver',
        status: error.response?.status || 500
      };
    }
  },

  // Update driver status
  async updateDriverStatus(driverId: number, isAvailable: boolean): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.patch(`/auth/users/${driverId}/`, {
        is_available: isAvailable
      });
      return { data: response.data, status: response.status };
    } catch (error: any) {
      return {
        error: error.response?.data?.detail || 'Failed to update driver status',
        status: error.response?.status || 500
      };
    }
  },

  assignRole: async (userId: number, role: string) => {
    return apiClient.post(`/users/${userId}/assign_role/`, { role });
  },

  removeRole: async (userId: number, role: string) => {
    return apiClient.post(`/users/${userId}/remove_role/`, { role });
  },

  getGroups: async () => {
    return apiClient.get('/groups/');
  },

};

export default userAPI;